import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../../services/api/axios";

/**
 * Build filter query params
 * @param {Object} filters
 */
const buildFilterParams = (filters) => {
  const params = {
    page: filters.page || 1,
    limit: filters.limit || 10,
  };

  // Provider
  if (filters.provider) {
    if (Array.isArray(filters.provider)) {
      filters.provider.forEach((p, i) => {
        params[`filter.provider[${i}]`] = p;
      });
    } else {
      params["filter.provider"] = filters.provider;
    }
  }

  // Status
  // allow empty string as well, but filters.status handles truthfully
  if (filters.status) {
    if (Array.isArray(filters.status)) {
      filters.status.forEach((s, i) => {
        params[`filter.status[${i}]`] = s;
      });
    } else {
      params["filter.status"] = filters.status;
    }
  }

  // Date range filters
  if (filters.createdAt && Array.isArray(filters.createdAt)) {
    filters.createdAt.forEach((d, i) => {
      params[`filter.createdAt[${i}]`] = d;
    });
  } else if (filters.createdAt) {
    params["filter.createdAt"] = filters.createdAt;
  }

  // Sorting
  if (filters.sortBy && Array.isArray(filters.sortBy)) {
    filters.sortBy.forEach((s, i) => {
      params[`sortBy[${i}]`] = s;
    });
  }

  // Search
  if (filters.search) {
    params.search = filters.search;
  }

  // Search fields
  if (filters.searchBy && Array.isArray(filters.searchBy)) {
    filters.searchBy.forEach((field, i) => {
      params[`searchBy[${i}]`] = field;
    });
  }

  // Field selection
  if (filters.select) {
    params.select = filters.select;
  }

  return params;
};

// Fetch webhooks
const fetchWebhooks = async (filters = {}) => {
  const response = await api.get("/v1/admin/business/webhooks", {
    params: buildFilterParams(filters),
    paramsSerializer: { indexes: null }, // Prevents bracket indexing for arrays if needed
  });
  return response.data;
};

// React Query hook
export const useWebhooks = (filters = {}) => {
  return useQuery({
    queryKey: ["webhooks", filters],
    queryFn: () => fetchWebhooks(filters),
    refetchOnWindowFocus: false,
  });
};

// retrigger webhooks
const retriggerWebhooks = async (uuid) => {
  const { data } = await api.post(
    `/v1/admin/business/webhooks/retrigger/${uuid}`,
  );
  return data;
};

export const useRetriggerWebhooks = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: retriggerWebhooks,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["webhooks"] });
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ["webhooks"] });
    },
  });
};
